import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { nomeDaSessao, signOutNow, useSession } from "@/lib/auth";
import { DATABASE_ID } from "@/lib/firebase";
import { capturar, EXTENSAO, prepararAvatar, type FotoEscolhida } from "@/lib/image";
import { Recortador } from "@/ui/Recortador";
import { subir } from "@/lib/upload";
import { photoURL } from "@/data/plates";
import {
  desbloquear, watchListaDeBloqueados, type Bloqueado,
} from "@/data/moderacao";
import { apagarConta, PALAVRA_PARA_APAGAR } from "@/data/users";
import {
  AVISOS_PADRAO, AVISOS_ROTULOS, salvarLembretes, saveAvisos, updateProfile,
  watchUser, type Avisos, type Perfil,
} from "@/data/users";
import {
  acharModelo, algumLembreteLigado, LEMBRETES_PADRAO, normalizarHora,
  resolverLembretes, SEQUENCIA_MINIMA_PRA_AVISAR,
  type Lembrete, type LembreteId, type Lembretes,
} from "@nhomi/core";
import { usePlano } from "@/lib/plano";
import { noAppDaPlay } from "@/lib/plataforma";
import * as push from "@/lib/push";
import { Avatar, Spinner, TopBar } from "@/ui/primitives";
import { descreverFalha, FalhaCard, type Falha } from "@/ui/Falha";
import { IconChevron, IconPencil } from "@/ui/icons";
import { SeloAssinante } from "@/ui/primitives";

/* ============================================================
   Minha conta

   Apelido, foto e avisos. O apelido e a foto daqui vencem os do Google:
   quem escolheu "Gu" não quer ver "Gustavo Moura Souza" de volta na
   próxima abertura do app.

   O interruptor de notificação mostra o estado REAL deste aparelho, que
   é "o token está gravado no banco" — não "o navegador deu permissão".
   No app anterior a tela dizia ligado com base na permissão e nada
   chegava, porque o registro tinha falhado em silêncio.
   ============================================================ */

export function Account() {
  const { user } = useSession();
  const { assinante } = usePlano();
  const [bloqueados, setBloqueados] = useState<Bloqueado[]>([]);
  const [apagando, setApagando] = useState(false);
  const [palavra, setPalavra] = useState("");
  const [erroAoApagar, setErroAoApagar] = useState("");
  const [indo, setIndo] = useState(false);
  const navigate = useNavigate();
  const uid = user?.uid ?? "";

  const [perfil, setPerfil] = useState<Perfil | null>(null);
  const [nome, setNome] = useState("");
  const [carregado, setCarregado] = useState(false);
  const [foto, setFoto] = useState<FotoEscolhida | null>(null);
  const [fotoPreview, setFotoPreview] = useState("");
  /* A foto recém-escolhida passa pelo corte (quadrado) antes de virar
     `foto`: o avatar é redondo, e a parte que aparece é a pessoa quem
     escolhe — não o meio da foto, que costumava ser o ombro. */
  const [paraCortar, setParaCortar] = useState<{ escolhida: FotoEscolhida; bytes: Blob } | null>(null);
  const [fotoAtual, setFotoAtual] = useState<string | null>(null);
  const arquivo = useRef<HTMLInputElement>(null);

  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(false);
  const [falha, setFalha] = useState<Falha | null>(null);

  const [avisos, setAvisos] = useState<Avisos>(AVISOS_PADRAO);
  const [lembretes, setLembretes] = useState<Lembretes>(LEMBRETES_PADRAO);
  /* A mensagem própria é digitada, e gravar a cada tecla seria uma
     escrita por letra. O rascunho vive aqui e desce ao banco no `blur`. */
  const [rascunhos, setRascunhos] = useState<Record<string, string>>({});
  const [estadoPush, setEstadoPush] = useState<push.Estado | null>(null);
  const [mexendoNoPush, setMexendoNoPush] = useState(false);
  const [recusa, setRecusa] = useState("");

  useEffect(() => {
    if (!user) return;
    return watchListaDeBloqueados(user.uid, setBloqueados);
  }, [user]);

  useEffect(() => {
    if (!uid) return;
    return watchUser(uid, setPerfil);
  }, [uid]);

  // Preenche uma vez: reescrever a cada snapshot apagaria o que está
  // sendo digitado, inclusive por causa da própria escrita.
  useEffect(() => {
    if (!perfil || carregado) return;
    setNome(perfil.name ?? "");
    setAvisos({ ...AVISOS_PADRAO, ...(perfil.notify ?? {}) });
    setLembretes(resolverLembretes(perfil.lembretes));
    setCarregado(true);
  }, [perfil, carregado]);

  /* A foto guardada pode ser um caminho do Storage (escolhida à mão) ou
     uma URL do Google (a que veio do login). As duas coexistem, então a
     tela precisa saber distinguir. */
  const fotoGuardada = perfil?.photo ?? "";
  useEffect(() => {
    if (!fotoGuardada) { setFotoAtual(null); return; }
    if (fotoGuardada.startsWith("http")) { setFotoAtual(fotoGuardada); return; }
    let vivo = true;
    void photoURL(fotoGuardada).then((u) => { if (vivo) setFotoAtual(u); });
    return () => { vivo = false; };
  }, [fotoGuardada]);

  /* A prévia sai dos bytes já copiados, não do `File`. Um object URL
     sobre o `File` da galeria do Android lê o `content://` na hora de
     desenhar — o mesmo ponteiro que morre — e a prévia quebrava junto
     com o envio, sem dizer por quê. */
  useEffect(() => {
    if (!foto) { setFotoPreview(""); return; }
    let vivo = true;
    let url = "";
    // A prévia é o avatar já cortado: o que vai subir, não a foto inteira.
    void prepararAvatar(foto)
      .then((bytes) => {
        if (!vivo) return;
        url = URL.createObjectURL(bytes);
        setFotoPreview(url);
      })
      .catch(() => { if (vivo) setFotoPreview(""); });
    return () => { vivo = false; if (url) URL.revokeObjectURL(url); };
  }, [foto]);

  useEffect(() => {
    if (!uid) return;
    let vivo = true;
    void push.estado(uid).then((e) => { if (vivo) setEstadoPush(e); });
    return () => { vivo = false; };
  }, [uid]);

  async function salvar() {
    if (!uid) return;
    setSalvando(true);
    setFalha(null);
    setSalvo(false);
    let etapa = "salvar o perfil";
    try {
      const patch: { name?: string; photo?: string } = {};
      const limpo = nome.trim();
      if (limpo && limpo !== perfil?.name) patch.name = limpo;

      if (foto) {
        etapa = "enviar a foto";
        /* A hora no nome do arquivo é o que faz a troca aparecer. O
           upload manda o navegador guardar a imagem por um ano
           (`immutable`), então um nome fixo continuaria servindo a foto
           velha pra todo mundo depois da troca. */
        const caminho = `users/${uid}/avatar-${Date.now()}.${EXTENSAO}`;
        await subir(caminho, await prepararAvatar(foto));
        patch.photo = caminho;
      }

      etapa = "salvar o perfil";
      await updateProfile(uid, patch);
      setFoto(null);
      setSalvo(true);
      setTimeout(() => setSalvo(false), 2500);
    } catch (err) {
      setFalha(descreverFalha(err, etapa));
    } finally {
      setSalvando(false);
    }
  }

  async function mexerNoPush(ligar: boolean) {
    if (!uid) return;
    setMexendoNoPush(true);
    setRecusa("");
    try {
      if (ligar) {
        const r = await push.ligar(uid);
        if (!r.ok && r.motivo) {
          setRecusa(push.EXPLICACAO[r.motivo] + (r.detalhe ? ` (${r.detalhe})` : ""));
        }
      } else {
        await push.desligar(uid);
      }
      setEstadoPush(await push.estado(uid));
    } finally {
      setMexendoNoPush(false);
    }
  }

  async function trocarAviso(id: keyof Avisos, valor: boolean) {
    if (!uid) return;
    const novos = { ...avisos, [id]: valor };
    setAvisos(novos);
    await saveAvisos(uid, novos).catch(() => {});
  }

  /* Salva na hora, como os outros interruptores desta tela: um botão
     "salvar" só para lembrete faria a pessoa mexer no horário, sair da
     tela e descobrir depois que nada tinha mudado. */
  async function mexerNoLembrete(id: LembreteId, patch: Partial<Lembrete>) {
    const novos: Lembretes = {
      ...lembretes,
      slots: lembretes.slots.map((s) => (s.id === id ? { ...s, ...patch } : s)),
    };
    setLembretes(novos);
    if (uid) await salvarLembretes(uid, novos).catch(() => {});
  }

  /**
   * A última chamada não é um slot, e por isso tem a própria função.
   *
   * Ela não pergunta se faltou o almoço — pergunta se o dia vai fechar
   * vazio com uma sequência em pé. Ver `packages/core/src/lembrete.ts`.
   */
  async function mexerNaUltimaChamada(patch: Partial<Lembretes["ultimaChamada"]>) {
    const novos: Lembretes = {
      ...lembretes,
      ultimaChamada: { ...lembretes.ultimaChamada, ...patch },
    };
    setLembretes(novos);
    if (uid) await salvarLembretes(uid, novos).catch(() => {});
  }

  async function mexerNoSoSeFaltar(valor: boolean) {
    const novos: Lembretes = { ...lembretes, soSeFaltar: valor };
    setLembretes(novos);
    if (uid) await salvarLembretes(uid, novos).catch(() => {});
  }

  const mudou = !!foto || (!!nome.trim() && nome.trim() !== perfil?.name);
  const previa = fotoPreview || fotoAtual || undefined;

  return (
    <main className="screen">
      <TopBar
        title="Minha conta"
        back="/"
        action={
          <button className="topbar-action" onClick={salvar} disabled={salvando || !mudou}>
            {salvando ? "Salvando…" : salvo ? "Salvo ✓" : "Salvar"}
          </button>
        }
      />

      <div className="screen-body no-tabbar">
        {falha && <FalhaCard falha={falha} />}

        <div className="profile-head">
          <div className="avatar-editavel">
            <Avatar name={nome || nomeDaSessao(user)} photo={previa} size="hero" />
            <button className="edit-btn" onClick={() => arquivo.current?.click()}
              aria-label="Trocar a foto">
              <IconPencil size={20} />
            </button>
          </div>
          <div className="sub">{user?.email ?? ""}</div>
        </div>
        <input ref={arquivo} type="file" accept="image/*" hidden
          onChange={(e) => {
            // Copia os bytes antes de soltar o input: o `File` do
            // Android perde o conteúdo e guarda os metadados.
            const f = e.target.files?.[0];
            const escolhida = f ? capturar(f) : null;
            e.target.value = "";
            if (escolhida) {
              void escolhida.bytes
                .then((bytes) => setParaCortar({ escolhida, bytes }))
                .catch(() => setFoto(escolhida));
            }
          }} />
        {paraCortar && (
          <Recortador foto={paraCortar.bytes} quadrado titulo="Sua foto"
            onCancelar={() => setParaCortar(null)}
            onPronto={(recorte) => { setFoto({ ...paraCortar.escolhida, recorte }); setParaCortar(null); }} />
        )}

        <div className="card">
          <label className="field">
            <span className="field-label">Como você aparece</span>
            <input value={nome} onChange={(e) => setNome(e.target.value)}
              maxLength={40} placeholder="Seu apelido" autoComplete="off" />
            <span className="field-hint">
              Vale em todos os desafios. Depois que você escolhe um apelido
              aqui, o nome do Google não sobrescreve mais.
            </span>
          </label>
        </div>

        {/* ---------- Nhomi Pro ----------

            A ÚNICA porta que não depende de bater num limite. Até aqui o
            Pro só aparecia quando a pessoa já tinha esbarrado no teto —
            quem está em um desafio só nunca via que ele existe, e não
            dava pra querer uma coisa que não se sabe que existe.

            Fica em Conta, e não na Home, porque a Home é o lugar de usar
            o app. Oferta permanente na tela principal vira banner, e a
            gente decidiu não ter banner nosso no caminho de ninguém. */}
        <Link className="card pro-linha" to="/assinar">
          <div className="pro-corpo">
            <div className="pro-titulo">
              Nhomi Pro {assinante && <SeloAssinante />}
            </div>
            <div className="pro-sub">
              {assinante
                ? "Ativo. Toque para ver ou cancelar."
                : "Desafios à vontade, sem anúncio e calorias sem racionar."}
            </div>
          </div>
          <span className="chev"><IconChevron size={18} /></span>
        </Link>

        {/* ---------- notificações ---------- */}

        <div className="section-label left">Notificações</div>

        {estadoPush === null ? <Spinner /> : (
          <>
            <div className="card">
              <div className="switch-row">
                <div className="switch-corpo">
                  <div className="switch-titulo">Neste aparelho</div>
                  <div className="switch-sub">
                    {estadoPush.salvo
                      ? "Ligadas — este aparelho recebe."
                      : estadoPush.comChave
                        ? "Desligadas neste aparelho."
                        : "Ainda não configuradas no projeto."}
                  </div>
                </div>
                <button
                  className={`switch ${estadoPush.salvo ? "on" : ""}`}
                  disabled={mexendoNoPush || !estadoPush.comChave || !estadoPush.suportado}
                  onClick={() => mexerNoPush(!estadoPush.salvo)}
                  role="switch"
                  aria-checked={estadoPush.salvo}
                  aria-label="Notificações neste aparelho">
                  <span />
                </button>
              </div>

              {recusa && <div className="hint-row erro">{recusa}</div>}

              {/* Ligadas pelo navegador, as notificações chegam COM A CARA
                  DO NAVEGADOR — nome dele, ícone dele e um "cancelar
                  inscrição" que não é nosso. Não é defeito, é de quem as
                  está desenhando; e quem ligou por ali merece saber antes
                  de estranhar. */}
              {!noAppDaPlay() && (
                <div className="hint-row">
                  Ligadas por aqui, elas chegam com o nome e o ícone do
                  navegador. Pelo app instalado da Play Store elas chegam
                  como Nhomi.
                </div>
              )}

              {!estadoPush.suportado && (
                <div className="hint-row">
                  Este navegador não recebe notificações. No iPhone elas só
                  funcionam com o app instalado na tela de início.
                </div>
              )}
            </div>

            <div className="card list-card">
              {AVISOS_ROTULOS.map(({ id, label, hint }) => (
                <div key={id} className="switch-row">
                  <div className="switch-corpo">
                    <div className="switch-titulo">{label}</div>
                    <div className="switch-sub">{hint}</div>
                  </div>
                  <button
                    className={`switch ${avisos[id] ? "on" : ""}`}
                    onClick={() => trocarAviso(id, !avisos[id])}
                    role="switch"
                    aria-checked={avisos[id]}
                    aria-label={label}>
                    <span />
                  </button>
                </div>
              ))}
            </div>
            <div className="hint-row">
              A escolha vale pra todos os seus aparelhos e é conferida no
              servidor antes de qualquer envio.
            </div>
          </>
        )}

        {/* ---------- lembretes de refeição ----------

            A diferença que explica esta seção inteira: os avisos de cima
            nascem de alguém ter feito alguma coisa num desafio, e por
            isso são configurados por TIPO de evento. Estes nascem do
            relógio, e por isso são configurados por HORÁRIO — e valem
            para a pessoa, não para cada grupo. Ver
            `packages/core/src/lembrete.ts`. */}

        <div className="section-label left">Lembretes de refeição</div>

        {/* O lembrete chega pelo mesmo canal dos outros avisos. Sem o
            aparelho registrado, ligar o almoço aqui seria uma promessa
            que nada cumpre — e prometer sem entregar é o defeito que
            esta tela inteira foi escrita pra não repetir. */}
        {estadoPush && !estadoPush.salvo && (
          <div className={`hint-row ${algumLembreteLigado(lembretes) ? "erro" : ""}`}>
            Os lembretes chegam pelas notificações, e elas estão desligadas
            neste aparelho. Ligue em "Neste aparelho", aqui em cima.
          </div>
        )}

        <div className="card list-card">
          {lembretes.slots.map((slot) => {
            const modelo = acharModelo(slot.id);
            return (
              <div key={slot.id} className="lembrete-bloco">
                <div className="switch-row">
                  <div className="switch-corpo">
                    <div className="switch-titulo">
                      {modelo?.emoji} {modelo?.label}
                    </div>
                    <div className="switch-sub">
                      {slot.ligado ? `Todo dia às ${slot.hora}` : "Desligado"}
                    </div>
                  </div>
                  <button
                    className={`switch ${slot.ligado ? "on" : ""}`}
                    onClick={() => mexerNoLembrete(slot.id, { ligado: !slot.ligado })}
                    role="switch"
                    aria-checked={slot.ligado}
                    aria-label={modelo?.label ?? slot.id}>
                    <span />
                  </button>
                </div>

                {slot.ligado && (
                  <div className="lembrete-detalhe">
                    <label className="field">
                      <span className="field-label">Horário</span>
                      {/* Sem `step`: o minuto escolhido é o minuto que
                          vale. A primeira versão arredondava para a grade
                          de 15 minutos, e quem marcava 19:55 via a tela
                          responder 19:45 — que se lê como "não salvou".
                          O relógio do Android ignora `step` de qualquer
                          forma, então a grade só existiria pra desmentir
                          o que a pessoa acabou de escolher. */}
                      <input type="time" className="lembrete-hora"
                        value={slot.hora}
                        onChange={(e) => mexerNoLembrete(slot.id, {
                          hora: normalizarHora(e.target.value),
                        })} />
                    </label>
                    <label className="field">
                      <span className="field-label">
                        Mensagem <i className="opcional">opcional</i>
                      </span>
                      <input
                        value={rascunhos[slot.id] ?? slot.texto}
                        placeholder={modelo?.corpo ?? ""}
                        maxLength={120}
                        autoComplete="off"
                        onChange={(e) => setRascunhos((r) => ({
                          ...r, [slot.id]: e.target.value,
                        }))}
                        onBlur={(e) => {
                          const limpo = e.target.value.trim();
                          setRascunhos((r) => ({ ...r, [slot.id]: limpo }));
                          void mexerNoLembrete(slot.id, { texto: limpo });
                        }} />
                    </label>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="card">
          <div className="switch-row">
            <div className="switch-corpo">
              <div className="switch-titulo">Só quando faltar</div>
              <div className="switch-sub">
                Não avisa se você já registrou aquela refeição hoje.
              </div>
            </div>
            <button
              className={`switch ${lembretes.soSeFaltar ? "on" : ""}`}
              onClick={() => mexerNoSoSeFaltar(!lembretes.soSeFaltar)}
              role="switch"
              aria-checked={lembretes.soSeFaltar}
              aria-label="Só quando faltar">
              <span />
            </button>
          </div>
        </div>

        <div className="hint-row">
          Chega <b>um aviso por refeição</b>, mesmo que você esteja em dez
          desafios. O horário é o deste aparelho, e o lembrete só sai se
          você tiver algum desafio rolando — podendo chegar alguns minutos
          depois da hora marcada.
        </div>

        {/* ---------- última chamada ----------

            Fica FORA da lista de refeições porque não é uma delas: entre
            a janta e a meia-noite não havia nada, e quem tinha seis dias
            seguidos perdia a sequência sem nenhum aviso. Ela não pergunta
            o que você comeu, pergunta se o dia vai fechar vazio. */}
        <div className="section-label left">Última chamada</div>

        <div className="card">
          <div className="switch-row">
            <div className="switch-corpo">
              <div className="switch-titulo">🔥 Avisar antes de perder a sequência</div>
              <div className="switch-sub">
                Se o dia estiver sem prato nenhum e você tiver{" "}
                {SEQUENCIA_MINIMA_PRA_AVISAR} dias seguidos ou mais.
              </div>
            </div>
            <button
              className={`switch ${lembretes.ultimaChamada.ligado ? "on" : ""}`}
              onClick={() => mexerNaUltimaChamada({ ligado: !lembretes.ultimaChamada.ligado })}
              role="switch"
              aria-checked={lembretes.ultimaChamada.ligado}
              aria-label="Avisar antes de perder a sequência">
              <span />
            </button>
          </div>

          {lembretes.ultimaChamada.ligado && (
            <div className="lembrete-detalhe">
              <span className="field-label">Hora</span>
              <input type="time" className="lembrete-hora"
                value={lembretes.ultimaChamada.hora}
                onChange={(e) => void mexerNaUltimaChamada({
                  hora: normalizarHora(e.target.value),
                })} />
            </div>
          )}
        </div>

        <div className="hint-row">
          Sai <b>uma vez por dia, no máximo</b>, e não sai se você já tiver
          postado qualquer prato — em qualquer desafio. Dia coberto por
          vale-falta conta como dia cumprido.
        </div>

        {/* Bloquear sem ter onde desbloquear é armadilha: a pessoa
            bloqueia num impulso e não acha mais o caminho de volta. */}
        {bloqueados.length > 0 && (
          <>
            <div className="section-label left">Bloqueados</div>
            <div className="card list-card">
              {bloqueados.map((b) => (
                <div key={b.uid} className="list-row">
                  <span className="label">{b.nome}</span>
                  <button className="btn-mini" onClick={() => {
                    if (user) void desbloquear(user.uid, b.uid);
                  }}>
                    Desbloquear
                  </button>
                </div>
              ))}
            </div>
            <div className="hint-row">
              Quem você bloqueia some do feed, dos comentários e do
              bate-papo — só pra você, e sem aviso pra pessoa.
            </div>
          </>
        )}

        <button className="btn btn-outline danger"
          onClick={async () => { await signOutNow(); navigate("/entrar", { replace: true }); }}>
          Sair da conta
        </button>

        <div className="card list-card">
          <Link className="list-row" to="/privacidade">
            <span className="label">Política de privacidade</span>
            <span className="chev"><IconChevron size={18} /></span>
          </Link>
          <Link className="list-row" to="/sobre">
            <span className="label">Sobre o Nhomi</span>
            <span className="chev"><IconChevron size={18} /></span>
          </Link>
        </div>

        {/* Apagar a conta é exigência de loja e precisa estar fácil de
            achar — mas não fácil de tocar sem querer. Digitar a palavra é
            a mesma régua da migração: escolher numa lista é um toque,
            digitar é uma decisão. */}
        {!apagando ? (
          <button className="btn-texto danger" onClick={() => setApagando(true)}>
            Apagar minha conta
          </button>
        ) : (
          <div className="card apagar-conta">
            <div className="folha-titulo">Apagar a conta</div>
            <p className="folha-texto">
              Somem o seu perfil, os seus pratos e as suas fotos, e você sai
              de todos os desafios. Desafio que você criou passa para quem
              entrou primeiro; se você estiver sozinho nele, ele some junto.
              <strong> Isso não tem desfazer.</strong>
            </p>
            <p className="folha-texto">
              Para confirmar, digite <strong>{PALAVRA_PARA_APAGAR}</strong>.
            </p>
            <input className="folha-campo" value={palavra} autoCapitalize="characters"
              placeholder={PALAVRA_PARA_APAGAR}
              onChange={(e) => setPalavra(e.target.value)} />
            {erroAoApagar && <div className="hint-row erro">{erroAoApagar}</div>}
            <button className="btn btn-primary danger"
              disabled={palavra.trim().toUpperCase() !== PALAVRA_PARA_APAGAR || indo}
              onClick={async () => {
                setIndo(true);
                setErroAoApagar("");
                try {
                  await apagarConta(palavra.trim().toUpperCase());
                  await signOutNow();
                  navigate("/entrar", { replace: true });
                } catch (err) {
                  // A mensagem do servidor distingue "faltou a palavra" de
                  // "deu erro no meio", e as duas pedem coisas diferentes.
                  setErroAoApagar((err as Error).message || "Não deu pra apagar agora.");
                  setIndo(false);
                }
              }}>
              {indo ? "Apagando…" : "Apagar a conta pra sempre"}
            </button>
            <button className="btn btn-white" onClick={() => {
              setApagando(false); setPalavra(""); setErroAoApagar("");
            }}>
              Cancelar
            </button>
          </div>
        )}

        <p className="fine center">Nhomi · banco {DATABASE_ID}</p>
        <div className="gap" />
      </div>
    </main>
  );
}
